import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { ImageSwiperRange } from '@/components/ImageSwiperRange/ImageSwiperRange';
import { amplitudeModule } from '@/modules/amplitude';
import { queryParamsModule } from '@/modules/queryParams';
import { webCampaign } from '@/modules/webCampaign';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingSubtitle } from '@/pages/Onboarding/components/Subtitle';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { AI_DATA } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OnboardingParamsTool } from '@/pages/Onboarding/utils/paramsTool';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataKeyType, LayoutDataType } from '../../types';

import { AICharacteristicStyles } from './AICharacteristic.styles';

export const ScreenAICharacteristic: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'AICharacteristic',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const aiCharacteristic = form.watch('ai_characteristic');
  const aiCharacteristicData = AI_DATA[webCampaign.aiType].characteristics;
  const aiCharacteristicIndexInForm = aiCharacteristicData.findIndex(
    (data) => data.label === aiCharacteristic,
  );
  const defaultAiCharacteristicIndex =
    aiCharacteristicIndexInForm === -1 ? 1 : aiCharacteristicIndexInForm;

  React.useEffect(() => {
    const defaultValue =
      aiCharacteristicData[defaultAiCharacteristicIndex].label;

    form.setValue('ai_characteristic', defaultValue);
    queryParamsModule.setParam<LayoutDataKeyType>(
      'ai_characteristic',
      defaultValue,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeInput = (e: CustomEvent) => {
    const value = (e.detail.value as number) || 0;
    const currentCharacteristic = aiCharacteristicData[value].label;

    form.setValue('ai_characteristic', currentCharacteristic);
  };

  const onRangeChange = (e: CustomEvent) => {
    const value = (e.detail.value as number) || 0;

    queryParamsModule.setParam<LayoutDataKeyType>(
      'ai_characteristic',
      aiCharacteristicData[value].label,
    );
  };

  const handleNextStep = (): void => {
    userService.createOrUpdate({
      onboarding_data: {
        ai_characteristic: aiCharacteristic,
      },
    });
    wizard.nextStep();
    OnboardingParamsTool.setParam('step', 'security');
  };

  return (
    <AICharacteristicStyles>
      <OnboardingNavigation />
      <OnboardingTitle>
        How do you want your AI character to be?
      </OnboardingTitle>
      <OnboardingSubtitle>
        Select the most exciting option now
      </OnboardingSubtitle>

      <IonContent>
        <ImageSwiperRange
          defaultIndex={defaultAiCharacteristicIndex}
          onRangeInput={onRangeInput}
          onRangeChange={onRangeChange}
          pictures={aiCharacteristicData}
        />
      </IonContent>

      <OnboardingFooter
        onContinue={handleNextStep}
        disabled={!aiCharacteristic}
      />
    </AICharacteristicStyles>
  );
};
